import { openDB } from "idb";

// Local-first cache for announcer-fe (#641). Read-only: unlike scribe-fe there
// is no outbox — the announcer never writes anything (docs/score/announcer.md
// "Explicitly out of scope"). This module owns the IndexedDB schema + a small
// repository API, same shape as scribe-fe's local/db.js.
//
//   entity stores — shows/classes/rides/tests/people/horses/breaks, keyed by
//                   server id, filled by the setup-time day prefetch.
//   meta          — sync cursors (e.g. lastPrefetchedAt).

export const DB_NAME = "announcer-offline";
// v2 (#705) adds the `results` store so class placings/composites survive
// offline — the upgrade below is additive (it only creates missing stores).
export const DB_VERSION = 2;

export const STORES = {
  shows: { keyPath: "id" },
  classes: { keyPath: "id", indexes: { showId: "showId" } },
  rides: { keyPath: "id", indexes: { classId: "classId" } },
  tests: { keyPath: "id" },
  people: { keyPath: "id" },
  horses: { keyPath: "id" },
  breaks: { keyPath: "id", indexes: { showId: "showId" } },
  // #705 — one row per class: { classId, rows: <GET /class/:id/results array> }.
  // The array is cached VERBATIM, so a PARTIAL row still carries no composite
  // (score-api withholds it, results.js exposeScores) and the #697 reveal gate
  // keys on the cached row's own status — nothing withheld can leak from cache.
  results: { keyPath: "classId" },
  meta: { keyPath: "key" },
};

let dbPromise;

export function openDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        for (const [name, cfg] of Object.entries(STORES)) {
          const store = db.objectStoreNames.contains(name)
            ? undefined
            : db.createObjectStore(name, { keyPath: cfg.keyPath });
          if (!store) continue;
          for (const [idxName, keyPath] of Object.entries(cfg.indexes ?? {})) {
            if (!store.indexNames.contains(idxName)) {
              store.createIndex(idxName, keyPath);
            }
          }
        }
      },
    });
  }
  return dbPromise;
}

// ── Generic repository ────────────────────────────────────────────────────

export async function get(store, key) {
  return (await openDb()).get(store, key);
}

export async function getAll(store) {
  return (await openDb()).getAll(store);
}

export async function bulkPut(store, values) {
  if (!values?.length) return;
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  for (const v of values) tx.store.put(v);
  await tx.done;
}

export async function clearStore(store) {
  return (await openDb()).clear(store);
}

export async function queryByIndex(store, index, value) {
  return (await openDb()).getAllFromIndex(store, index, value);
}

// ── Meta (prefetch cursor) ──────────────────────────────────────────────────

export async function getMeta(key) {
  const row = await get("meta", key);
  return row ? row.value : undefined;
}

export async function setMeta(key, value) {
  return (await openDb()).put("meta", { key, value });
}

// ── Per-entity accessors (read hooks build on these, #642+) ────────────────

export const shows = {
  get: (id) => get("shows", id),
  getAll: () => getAll("shows"),
  putAll: (rows) => bulkPut("shows", rows),
};

export const classes = {
  get: (id) => get("classes", id),
  getAll: () => getAll("classes"),
  byShow: (showId) => queryByIndex("classes", "showId", showId),
  putAll: (rows) => bulkPut("classes", rows),
  clear: () => clearStore("classes"),
};

export const rides = {
  get: (id) => get("rides", id),
  getAll: () => getAll("rides"),
  byClass: (classId) => queryByIndex("rides", "classId", classId),
  putAll: (rows) => bulkPut("rides", rows),
  clear: () => clearStore("rides"),
};

export const tests = {
  get: (id) => get("tests", id),
  getAll: () => getAll("tests"),
  putAll: (rows) => bulkPut("tests", rows),
  clear: () => clearStore("tests"),
};

export const people = {
  get: (id) => get("people", id),
  getAll: () => getAll("people"),
  putAll: (rows) => bulkPut("people", rows),
};

export const horses = {
  get: (id) => get("horses", id),
  getAll: () => getAll("horses"),
  putAll: (rows) => bulkPut("horses", rows),
};

export const breaks = {
  get: (id) => get("breaks", id),
  getAll: () => getAll("breaks"),
  byShow: (showId) => queryByIndex("breaks", "showId", showId),
  putAll: (rows) => bulkPut("breaks", rows),
  clear: () => clearStore("breaks"),
};

export const results = {
  get: (classId) => get("results", classId),
  put: async (classId, rows) =>
    (await openDb()).put("results", { classId, rows: rows ?? [] }),
  clear: () => clearStore("results"),
};

// Test-only: drop the cached connection + delete the database so each test
// starts from a known-empty state.
export async function _resetDbForTests() {
  if (dbPromise) (await dbPromise).close();
  dbPromise = undefined;
  await new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}
